import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { WarningOctagon, Trash, Package, Key, Desktop } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Pertes = () => {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/losses`, { withCredentials: true });
      setItems(r.data);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirm({
      title: 'Supprimer cette entrée de perte ?',
      description: 'Cette suppression ne restaurera PAS le stock. Elle ne doit servir qu\'à corriger une erreur de saisie.',
      destructive: true,
    }))) return;
    try {
      await axios.delete(`${API_URL}/api/losses/${id}`, { withCredentials: true });
      toast.success('Entrée supprimée');
      load();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const totalValue = items.reduce((s, it) => s + (it.total_loss_value || 0), 0);

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="pertes-page">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Pertes — Mise au rebut</h1>
          <p className="text-slate-600">Historique des produits retirés du stock avec justification (casse, péremption, vol, etc.)</p>
        </div>

        <div className="mb-4 inline-flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <WarningOctagon size={20} className="text-red-600" />
          <span className="text-sm text-red-900">
            <strong>{items.length}</strong> entrée(s) — Valeur totale des pertes (prix d&apos;achat) : <strong>{totalValue.toFixed(2)} €</strong>
          </span>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center text-slate-500">
            <Package size={48} className="mx-auto text-slate-300 mb-3" />
            Aucune perte enregistrée
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-xs uppercase text-slate-500">Date</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-slate-500">Produit</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-slate-500">Quantité</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-slate-500">Prix achat unit.</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-slate-500">Valeur perte</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-slate-500">Justification</th>
                  <th className="text-left py-3 px-4 text-xs uppercase text-slate-500">Saisi par</th>
                  <th className="text-right py-3 px-4 text-xs uppercase text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const Icon = it.product_type === 'key' ? Key : Desktop;
                  return (
                    <tr key={it.id} data-testid={`loss-row-${it.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-700">{fmtDate(it.created_at)}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className="inline-flex items-center gap-2">
                          <Icon size={16} className="text-slate-500" />
                          <span className="font-medium">{it.product_name}</span>
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-right font-semibold text-red-700">-{it.quantity}</td>
                      <td className="py-3 px-4 text-sm text-right">{(it.unit_purchase_price || 0).toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-right font-medium">{(it.total_loss_value || 0).toFixed(2)} €</td>
                      <td className="py-3 px-4 text-sm text-slate-700 italic">{it.reason}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">{it.created_by}</td>
                      <td className="py-3 px-4 text-right">
                        {isAdmin && (
                          <button onClick={() => handleDelete(it.id)} data-testid={`delete-loss-${it.id}`} className="p-2 text-red-600 hover:bg-red-50 rounded">
                            <Trash size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Pertes;
